import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { EVENT_CSS_VARS } from '../styles/cssVariables';

/**
 * Outer container for expanded event card (hover popup)
 * Provides outline and background, contains MainContentRow and optional ActionFooter
 */
export const ExpandedCardWrapper = styled(Box)({
  // Dynamic appearance via CSS variables (matches parent event variant)
  outline: `var(${EVENT_CSS_VARS.expanded.outline.width}, 1px) var(${EVENT_CSS_VARS.expanded.outline.style}, solid) var(${EVENT_CSS_VARS.color.accent}, #ccc)`,
  outlineOffset: `calc(-1 * var(${EVENT_CSS_VARS.expanded.outline.width}, 0px))`,
  boxShadow: `var(${EVENT_CSS_VARS.expanded.shadow}, 0 8px 24px rgba(0, 0, 0, 0.25))`,

  // Static layout
  backgroundColor: 'var(--mui-palette-background-paper, #fff)',
  color: 'var(--mui-palette-text-primary, #000)',
  borderRadius: '8px',
  minWidth: '280px',
  maxWidth: '90vw',
  display: 'flex',
  flexDirection: 'column', // Stack MainContentRow and ActionFooter vertically
  boxSizing: 'border-box',
  overflow: 'hidden', // Ensure footer respects border radius
}) as typeof Box;

/**
 * Main content row - contains icon and content column side-by-side
 * Replaces the flexDirection: row from original ExpandedCardWrapper
 */
export const MainContentRow = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  gap: '8px',
  padding: '24px',
}) as typeof Box;

/**
 * Action footer for actionable variant
 * Full-width colored banner with action message
 */
export const ActionFooter = styled(Box)({
  backgroundColor: `var(${EVENT_CSS_VARS.color.accent}, #6C00CC)`,
  color: '#ffffff',
  padding: '12px 24px',
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: 1.4,
}) as typeof Box;

/**
 * Content column - contains all text content
 */
export const ContentColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px', // 0.5 * 8px
}) as typeof Box;

/**
 * Patient name as title
 */
export const PatientName = styled(Typography)({
  fontWeight: 600,
  lineHeight: 1.4,
  marginTop: '-1px',
}) as typeof Typography;

/**
 * Row for confirmation status indicator
 */
export const ConfirmationRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px', // 0.5 * 8px
}) as typeof Box;

/**
 * Icon box - contains event icon
 *
 * requires component prop of SvgIcon or similar
 */
export const Icon = styled(Box)({
  fontSize: '24px',
  color: `var(${EVENT_CSS_VARS.color.accent}, #6C00CC)`,
}) as typeof Box;

/**
 * Green checkmark icon for patient confirmation status
 */
export const ConfirmedIcon = styled(CheckCircleIcon)({
  fontSize: '1.4rem',
  color: '#33E081',
}) as unknown as typeof CheckCircleIcon;
